import { useIntl } from "react-intl";

import { AnimateSize } from "@nens/chakra/components";
import { humanizeBytes } from "@nens/shared-utils";
import { RasterTypeEnum } from "@nens/threedi/openapi-client";

import { DetailRow, DetailTable } from "@/components/detail-table";
import { DownloadIconLink } from "@/components/download-icon-link";
import { HeaderBox } from "@/components/header-box";
import { useHccRasterDownloadQueryDirectly } from "@/queries/query-hcc-raster-download";
import { useHccSqliteDownloadQueryDirectly } from "@/queries/query-hcc-sqlite-download";

import { useSchematisationDetailsContext } from "../schematisation-details-context";

// all possible downloadable file types that can be attached to a schematisation
const RASTER_TYPE_DISPLAY_NAMES: Record<RasterTypeEnum, string> = {
  [RasterTypeEnum.DEM_FILE]: "Digital Elevation Model",
  [RasterTypeEnum.DEM_RAW_FILE]: "Digital Elevation Model (raw)",
  [RasterTypeEnum.EQUILIBRIUM_INFILTRATION_RATE_FILE]: "Equilibrium Infiltration Rate",
  [RasterTypeEnum.FRICT_COEF_FILE]: "Friction Coefficient",
  [RasterTypeEnum.INITIAL_GROUNDWATER_LEVEL_FILE]: "Initial Groundwater Level",
  [RasterTypeEnum.INITIAL_WATERLEVEL_FILE]: "Initial Water Level",
  [RasterTypeEnum.GROUNDWATER_HYDRO_CONNECTIVITY_FILE]: "Groundwater Hydro Connectivity",
  [RasterTypeEnum.GROUNDWATER_IMPERVIOUS_LAYER_LEVEL_FILE]: "Groundwater Impervious Layer Level",
  [RasterTypeEnum.INFILTRATION_DECAY_PERIOD_FILE]: "Infiltration Decay Period",
  [RasterTypeEnum.INITIAL_INFILTRATION_RATE_FILE]: "Initial Infiltration Rate",
  [RasterTypeEnum.LEAKAGE_FILE]: "Leakage",
  [RasterTypeEnum.PHREATIC_STORAGE_CAPACITY_FILE]: "Phreatic Storage Capacity",
  [RasterTypeEnum.HYDRAULIC_CONDUCTIVITY_FILE]: "Hydraulic Conductivity",
  [RasterTypeEnum.POROSITY_FILE]: "Porosity",
  [RasterTypeEnum.INFILTRATION_RATE_FILE]: "Infiltration Rate",
  [RasterTypeEnum.MAX_INFILTRATION_CAPACITY_FILE]: "Max Infiltration Capacity",
  [RasterTypeEnum.INTERCEPTION_FILE]: "Interception",
  [RasterTypeEnum.VEGETATION_HEIGHT_FILE]: "Vegetation Height",
  [RasterTypeEnum.VEGETATION_DRAG_COEFFICIENT_FILE]: "Vegetation Drag Coefficient",
  [RasterTypeEnum.VEGETATION_STEM_COUNT_FILE]: "Vegetation Stem Count",
  [RasterTypeEnum.VEGETATION_STEM_DIAMETER_FILE]: "Vegetation Stem Diameter",
  [RasterTypeEnum.INITIAL_CONCENTRATION_FILE]: "Initial Concentration",
  [RasterTypeEnum.INITIAL_GROUNDWATER_CONCENTRATION_FILE]: "Initial Groundwater Concentration",
};

export function SchematisationDownloadsSection() {
  const { schematisation, revision } = useSchematisationDetailsContext();
  const schematisationId = schematisation.id;
  const revisionId = revision.id;

  const intl = useIntl();
  const sqliteDownload = useHccSqliteDownloadQueryDirectly();
  const rasterDownload = useHccRasterDownloadQueryDirectly();

  const rows: DetailRow[] = [];

  if (revision?.sqlite?.file) {
    const sqliteFile = revision.sqlite.file;
    const sqliteFilename = sqliteFile.filename ?? "sqlite.db";
    rows.push({
      name: intl.formatMessage({
        id: "file.downloads.schematisation_database",
        defaultMessage: "Model schematisation database",
      }),
      value: `${sqliteFilename} (${humanizeBytes(sqliteFile.size ?? 0)})`,
      action: (
        <DownloadIconLink
          filename={sqliteFilename}
          fetchUrl={async function fetchDownloadUrl() {
            const result = await sqliteDownload(schematisationId, revisionId);
            return result.isSuccess ? result.data : undefined;
          }}
        />
      ),
    });
  }

  if (revision?.rasters?.length) {
    revision.rasters.forEach((raster) => {
      if (!raster.file) return;
      const rasterFile = raster.file;
      const rasterFilename = rasterFile.filename ?? raster.name ?? "raster";
      rows.push({
        name:
          (raster.type != null ? RASTER_TYPE_DISPLAY_NAMES[raster.type] : undefined) ??
          raster.type ??
          intl.formatMessage({ id: "file.downloads.raster", defaultMessage: "Raster" }),
        value: `${rasterFilename} (${humanizeBytes(rasterFile.size ?? 0)})`,
        action: (
          <DownloadIconLink
            filename={rasterFilename}
            fetchUrl={async function fetchDownloadUrl() {
              if (!raster.id) return;
              const result = await rasterDownload({
                schematisationId,
                revisionId,
                raster,
              });
              return result.isSuccess ? result.data : undefined;
            }}
          />
        ),
      });
    });
  }

  if (rows.length === 0) return null;

  return (
    <AnimateSize>
      <HeaderBox
        name={intl.formatMessage({ id: "section.downloads", defaultMessage: "Downloads" })}
        boxHeight={0}
        mb={0}
        borderBottomWidth={0}
        borderBottomRadius="none"
      />
      <DetailTable rows={rows} />
    </AnimateSize>
  );
}
